import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Paper, Button, Card, CardContent, CardActions,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
  Alert, CircularProgress, IconButton, Stack, Divider, LinearProgress,
  Tooltip, Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as RunIcon,
  PlayCircle as RunAllIcon,
  Stop as StopIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Analytics as AnalyzeIcon,
  TrendingUp,
  TrendingDown,
  PauseCircleOutline,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TipsAndUpdates as TipsAndUpdatesIcon,
  Favorite as FavoriteIcon,
  AutoAwesome,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import StrategyAnalysis from '../features/strategies/StrategyAnalysis';
import { MARKET_IDS as MARKETS } from '../constants/markets';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const getAuth = () => {
  const tokensJson = localStorage.getItem('authTokens');
  if (tokensJson) {
    try {
      const tokens = JSON.parse(tokensJson);
      return tokens.access_token ? `Bearer ${tokens.access_token}` : '';
    } catch { /* ignore */ }
  }
  return '';
};

const api = async <T,>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  const { apiJson } = await import('../utils/apiFetch');
  return apiJson<T>(endpoint, {
    ...options,
    headers: { Authorization: getAuth(), ...options.headers },
  });
};

// --- Constants ---

const PARAM_LABELS: Record<string, string> = {
  amount_eur: 'Betrag €', position_size_pct: 'Position %', fast_ema: 'EMA schnell',
  mid_ema: 'EMA mittel', slow_ema: 'EMA langsam', ema_fast: 'EMA schnell', ema_slow: 'EMA langsam',
  rsi_period: 'RSI', atr_period: 'ATR', trailing_atr_multiplier: 'Trailing ×ATR',
  lookback_period: 'Rückblick', volume_multiplier: 'Vol. Faktor', atr_stop_multiplier: 'Stop ×ATR',
  bollinger_period: 'Bollinger', bollinger_std: 'BB σ', squeeze_lookback: 'Squeeze Rückbl.',
  squeeze_percentile: 'Squeeze %', roc_period: 'ROC', stoch_k_period: 'Stoch %K',
  stoch_d_period: 'Stoch %D', stoch_k: 'Stoch %K', stoch_d: 'Stoch %D',
  rsi_oversold: 'RSI Kauf', ema_short: 'EMA kurz', ema_long: 'EMA lang',
  stop_atr_multiplier: 'Stop ×ATR', stop_atr: 'Stop ×ATR', target_atr: 'Ziel ×ATR',
  max_scale_ins: 'Max Nachkauf', trailing_stop_atr: 'Trail ×ATR',
  rsi_entry: 'RSI Einstieg', volume_factor: 'Vol. Faktor',
};

// Standard Stop-Loss / Take-Profit pro Strategietyp
const SL_TP_DEFAULTS: Record<string, { sl: number; tp: number; slTip: string; tpTip: string }> = {
  marktmeister: { sl: 3, tp: 6, slTip: 'Der MarktMeister nutzt einen ATR-basierten Trailing Stop — 3% als Sicherheitsnetz schützt auch wenn der Bot sich verläuft.', tpTip: 'Der Bot nimmt Gewinne automatisch über den Signal-Aggregator mit. 6% als festes Ziel dient als zusätzliche Absicherung.' },
};

const STRATEGY_TYPES = [
  {
    value: 'marktmeister',
    label: 'MarktMeister',
    icon: <AutoAwesome />,
    description: 'Adaptiver Bot — erkennt Marktphasen und passt Strategie automatisch an.',
    color: '#7C4DFF',
    candles: '5m',
    defaultInterval: 300, // 5min
    defaultConfig: {},
  },
];

const INTERVAL_OPTIONS = [
  { value: 60, label: '1 Minute' },
  { value: 120, label: '2 Minuten' },
  { value: 300, label: '5 Minuten' },
  { value: 600, label: '10 Minuten' },
  { value: 900, label: '15 Minuten' },
  { value: 1800, label: '30 Minuten' },
  { value: 3600, label: '1 Stunde' },
  { value: 7200, label: '2 Stunden' },
  { value: 14400, label: '4 Stunden' },
  { value: 28800, label: '8 Stunden' },
  { value: 43200, label: '12 Stunden' },
  { value: 86400, label: '24 Stunden' },
];

const formatInterval = (seconds: number): string => {
  const opt = INTERVAL_OPTIONS.find(o => o.value === seconds);
  if (opt) return opt.label;
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} Min.`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} Std.`;
  return `${Math.round(seconds / 86400)} Tag(e)`;
};

// --- Types ---

interface Strategy {
  id: number;
  name: string;
  description: string | null;
  strategy_type: string;
  config: Record<string, any>;
  is_active: boolean;
  budget_eur: number | null;
  budget_used_eur: number;
  interval_seconds: number;
  max_position_size: number | null;
  stop_loss_percentage: number | null;
  take_profit_percentage: number | null;
  min_trade_amount_eur: number | null;
  min_trade_behavior: 'skip' | 'round_up' | null;
  total_trades: number;
  winning_trades: number;
  total_profit_loss: number;
  last_executed: string | null;
  is_live: boolean;
  last_signal: { action: string; reason: string } | null;
  last_signal_at: string | null;
}

interface ExecutionResult {
  success: boolean;
  signals: any[];
  trades_executed: any[];
  reason: string | null;
}

// --- Signal display helper ---
const SIGNAL_CONFIG: Record<string, {
  severity: 'success' | 'error' | 'info' | 'warning';
  label: string;
  icon: React.ReactElement;
}> = {
  buy: { severity: 'success', label: 'KAUF', icon: <TrendingUp /> },
  sell: { severity: 'error', label: 'VERKAUF', icon: <TrendingDown /> },
  hold: { severity: 'info', label: 'HALTEN', icon: <PauseCircleOutline /> },
  error: { severity: 'warning', label: 'FEHLER', icon: <PauseCircleOutline /> },
};

const StrategySignalDisplay: React.FC<{
  signal: { action: string; reason: string } | null;
  signalAt: string | null;
}> = ({ signal, signalAt }) => {
  const [expanded, setExpanded] = React.useState(false);

  if (!signal) return null;

  const action = signal.action?.toLowerCase() || 'hold';
  const cfg = SIGNAL_CONFIG[action] || SIGNAL_CONFIG.hold;
  const reason = signal.reason || '';
  const reasonLines = reason.split('\n').filter(Boolean);
  const isLong = reason.length > 100 || reasonLines.length > 2;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
          Letztes Signal
        </Typography>
        {signalAt && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
            {new Date(signalAt).toLocaleString('de-DE', {
              day: '2-digit', month: '2-digit', year: '2-digit',
              hour: '2-digit', minute: '2-digit',
            })}
          </Typography>
        )}
      </Box>
      <Alert
        severity={cfg.severity}
        icon={cfg.icon}
        sx={{
          py: 0.5,
          borderRadius: 2,
          '& .MuiAlert-message': { width: '100%' },
          '& .MuiAlert-icon': { alignItems: 'center', mr: 1 },
        }}
        action={
          isLong ? (
            <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ mt: -0.5 }}>
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          ) : undefined
        }
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.75rem' }}>
          {cfg.label}
          {reason && (
            <Typography component="span" variant="body2" fontWeight={400} sx={{ ml: 0.5, fontSize: '0.75rem' }}>
              — {reasonLines[0]?.substring(0, 80)}{(!expanded && (reasonLines[0]?.length > 80 || reasonLines.length > 1)) ? '...' : ''}
            </Typography>
          )}
        </Typography>
        {isLong && (
          <Collapse in={expanded}>
            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line', opacity: 0.85, fontSize: '0.7rem' }}>
              {reason}
            </Typography>
          </Collapse>
        )}
      </Alert>
    </Box>
  );
};

// --- Component ---

const StrategiesPage: React.FC = () => {
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [runnerStatus, setRunnerStatus] = useState<{ running: boolean } | null>(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState<Strategy | null>(null);
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState('trailing_trend');
  const [formMarket, setFormMarket] = useState('BTC-EUR');
  const [formActive, setFormActive] = useState(true);
  const [formDescription, setFormDescription] = useState('');
  const [formBudget, setFormBudget] = useState<string>('');
  const [formStopLoss, setFormStopLoss] = useState<string>('');
  const [formTakeProfit, setFormTakeProfit] = useState<string>('');
  const [formInterval, setFormInterval] = useState<number>(3600);
  // Mindest-Trade-Betrag pro Strategie. Verhindert dass die 0,25%-Bitvavo-Fee
  // die Rendite kleiner Trades aufzehrt.
  const [formMinTradeAmount, setFormMinTradeAmount] = useState<string>('');
  const [formMinTradeBehavior, setFormMinTradeBehavior] = useState<'skip' | 'round_up'>('skip');
  // Bear-Hunter-Modus: Wenn aktiv, oeffnet die Strategie bei TREND_DOWN
  // Short-Positionen statt nur ins Cash zu gehen. Default deaktiviert,
  // weil asymmetrisches Risiko + Bitvavo-Borrow-Fees.
  // Greift nur fuer marktmeister; andere Strategien ignorieren das Feld.
  const [formEnableShorts, setFormEnableShorts] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);
  const [marketVolatility, setMarketVolatility] = useState<Record<string, { volatility_pct: number; label: string; color: string; price: number; change_24h: number }>>({});

  // Execution state
  const [executing, setExecuting] = useState<number | null>(null);
  const [executingAll, setExecutingAll] = useState(false);
  const [lastResult, setLastResult] = useState<{ id: number; result: ExecutionResult } | null>(null);

  // Analysis dialog state
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [analysisStrategy, setAnalysisStrategy] = useState<Strategy | null>(null);

  // Strategy valuations (current portfolio value per strategy)
  const [valuations, setValuations] = useState<Record<string, { holdings: number; holdings_value_eur: number; current_value_eur: number; budget_eur: number; pnl_percent: number; market_price: number | null; market: string }>>({});

  // Best Advice state
  const [adviceLoading, setAdviceLoading] = useState(false);
  const [adviceResults, setAdviceResults] = useState<any[] | null>(null);
  const [adviceDialogOpen, setAdviceDialogOpen] = useState(false);

  const openAnalysis = (s: Strategy) => {
    setAnalysisStrategy(s);
    setAnalysisOpen(true);
  };

  const loadStrategies = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api<Strategy[]>('/strategies/');
      setStrategies(data);
      api<typeof valuations>('/strategies/valuations').then(setValuations).catch(() => {});
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStrategies();
    api<any>('/paper/runner/status').then(setRunnerStatus).catch(() => {});
  }, [loadStrategies]);

  // --- CRUD ---

  const openCreateDialog = () => {
    const defaultType = 'trailing_trend';
    const defaults = SL_TP_DEFAULTS[defaultType];
    setEditingStrategy(null);
    setFormName('');
    setFormType(defaultType);
    setFormMarket('BTC-EUR');
    setFormActive(true);
    setFormDescription('');
    setFormBudget('');
    setFormStopLoss(defaults ? String(defaults.sl) : '');
    setFormTakeProfit(defaults ? String(defaults.tp) : '');
    setFormMinTradeAmount('');
    setFormMinTradeBehavior('skip');
    setFormEnableShorts(false);
    setFormInterval(STRATEGY_TYPES.find(t => t.value === defaultType)?.defaultInterval || 3600);
    setDialogOpen(true);
    // Load market volatility
    api<any>('/paper/market-volatility').then(setMarketVolatility).catch(() => {});
  };

  const openEditDialog = (s: Strategy) => {
    setEditingStrategy(s);
    setFormName(s.name);
    setFormType(s.strategy_type);
    setFormMarket(s.config.market || 'BTC-EUR');
    setFormActive(s.is_active);
    setFormDescription(s.description || '');
    setFormBudget(s.budget_eur ? String(s.budget_eur) : '');
    setFormStopLoss(s.stop_loss_percentage ? String(s.stop_loss_percentage) : '');
    setFormTakeProfit(s.take_profit_percentage ? String(s.take_profit_percentage) : '');
    setFormMinTradeAmount(s.min_trade_amount_eur ? String(s.min_trade_amount_eur) : '');
    setFormMinTradeBehavior((s.min_trade_behavior as 'skip' | 'round_up') || 'skip');
    // enable_shorts liegt in config (nicht als eigenes Strategy-Feld)
    setFormEnableShorts(Boolean(s.config?.enable_shorts));
    setFormInterval(s.interval_seconds || 300);
    setDialogOpen(true);
    // Load market volatility
    api<any>('/paper/market-volatility').then(setMarketVolatility).catch(() => {});
  };

  // Sync interval + SL/TP defaults when strategy type changes in create mode
  const handleTypeChange = (newType: string) => {
    setFormType(newType);
    if (!editingStrategy) {
      const typeDef = STRATEGY_TYPES.find(t => t.value === newType);
      if (typeDef) setFormInterval(typeDef.defaultInterval);
      const defaults = SL_TP_DEFAULTS[newType];
      if (defaults) {
        setFormStopLoss(String(defaults.sl));
        setFormTakeProfit(String(defaults.tp));
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const typeDef = STRATEGY_TYPES.find(t => t.value === formType);
      const budgetVal = formBudget ? parseFloat(formBudget) : null;
      const slVal = formStopLoss ? parseFloat(formStopLoss) : null;
      const tpVal = formTakeProfit ? parseFloat(formTakeProfit) : null;
      const minAmountRaw = formMinTradeAmount ? parseFloat(formMinTradeAmount) : 0;
      // 0 oder leer = kein Min-Limit; >0 sendet Wert + Behavior, sonst NULL
      const minAmount = minAmountRaw > 0 ? minAmountRaw : null;
      const minBehavior = minAmountRaw > 0 ? formMinTradeBehavior : null;

      // Validierung: Bitvavo-Mindest-Orderwert ist 5 EUR
      if (minAmount !== null && minAmount < 5) {
        setSaving(false);
        setError('Mindest-Trade-Betrag muss mindestens 5 EUR sein (Bitvavo-Limit).');
        return;
      }

      if (editingStrategy) {
        // Allow full editing — including type change and budget — even for active strategies
        const newTypeDef = STRATEGY_TYPES.find(t => t.value === formType);
        const baseConfig = formType !== editingStrategy.strategy_type
          ? { ...newTypeDef?.defaultConfig, market: formMarket }
          : { ...editingStrategy.config, market: formMarket };
        // Bear-Hunter-Toggle nur fuer marktmeister relevant; andere
        // Strategien lesen das Feld nicht aus.
        const configWithShorts = formType === 'marktmeister'
          ? { ...baseConfig, enable_shorts: formEnableShorts }
          : baseConfig;

        await api(`/strategies/${editingStrategy.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: formName,
            description: formDescription,
            strategy_type: formType !== editingStrategy.strategy_type ? formType : undefined,
            config: configWithShorts,
            budget_eur: budgetVal,
            stop_loss_percentage: slVal,
            take_profit_percentage: tpVal,
            min_trade_amount_eur: minAmount,
            min_trade_behavior: minBehavior,
            interval_seconds: formInterval,
          }),
        });
        setSuccess(`"${formName}" aktualisiert${editingStrategy.is_active ? ' (auch während aktiv)' : ''}`);
      } else {
        const config = { ...typeDef?.defaultConfig, market: formMarket };
        const configWithShorts = formType === 'marktmeister'
          ? { ...config, enable_shorts: formEnableShorts }
          : config;
        await api('/strategies/', {
          method: 'POST',
          body: JSON.stringify({
            name: formName,
            description: formDescription,
            strategy_type: formType,
            config: configWithShorts,
            budget_eur: budgetVal,
            stop_loss_percentage: slVal,
            take_profit_percentage: tpVal,
            min_trade_amount_eur: minAmount,
            min_trade_behavior: minBehavior,
            interval_seconds: formInterval,
          }),
        });
        setSuccess(`"${formName}" erstellt`);
      }
      setDialogOpen(false);
      await loadStrategies();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Strategie "${name}" wirklich löschen?`)) return;
    try {
      await api(`/strategies/${id}`, { method: 'DELETE' });
      setSuccess(`"${name}" gelöscht`);
      await loadStrategies();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleActive = async (s: Strategy) => {
    try {
      const endpoint = s.is_active ? 'deactivate' : 'activate';
      await api(`/strategies/${s.id}/${endpoint}`, { method: 'POST' });
      await loadStrategies();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleLive = async (strategyId: number, newLiveState: boolean) => {
    if (newLiveState) {
      const confirmed = window.confirm(
        'ACHTUNG: Live-Trading aktivieren?\n\n' +
        'Die Strategie wird mit ECHTEM Geld auf der Bitvavo-Börse handeln!\n' +
        'Stelle sicher, dass deine API-Keys konfiguriert sind und du die Risiken verstehst.\n\n' +
        'Fortfahren?'
      );
      if (!confirmed) return;
    }

    try {
      await api(`/strategies/${strategyId}`, {
        method: 'PUT',
        body: JSON.stringify({ is_live: newLiveState }),
      });
      await loadStrategies();
    } catch (e: any) {
      setError(e.message || 'Fehler beim Umschalten');
    }
  };

  // --- Execution ---

  const handleExecute = async (id: number) => {
    setExecuting(id);
    setError(null);
    setLastResult(null);
    try {
      const result = await api<ExecutionResult>(`/strategies/${id}/execute`, { method: 'POST' });
      setLastResult({ id, result });
      setSuccess(`Strategie ausgeführt: ${result.trades_executed.length} Trade(s)`);
      await loadStrategies();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setExecuting(null);
    }
  };

  const handleExecuteAll = async () => {
    setExecutingAll(true);
    setError(null);
    try {
      const data = await api<{ results: any[]; total: number }>('/strategies/execute-all', { method: 'POST' });
      const totalTrades = data.results.reduce((sum: number, r: any) => sum + (r.trades_executed?.length || 0), 0);
      setSuccess(`Alle Strategien ausgeführt: ${totalTrades} Trade(s) aus ${data.total} Strategien`);
      await loadStrategies();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setExecutingAll(false);
    }
  };

  // --- Best Advice ---

  const handleBestAdvice = async () => {
    setAdviceLoading(true);
    setAdviceDialogOpen(true);
    setAdviceResults(null);
    try {
      const data = await api<any>('/paper/best-advice');
      setAdviceResults(data.recommendations || []);
    } catch (e: any) {
      setError(e.message || 'Marktanalyse fehlgeschlagen');
      setAdviceDialogOpen(false);
    } finally {
      setAdviceLoading(false);
    }
  };

  const handleCreateFromAdvice = async (rec: any) => {
    try {
      const config = { ...rec.optimized_config };
      // Remove internal metadata
      delete config._optimized;
      delete config._volatility_pct;
      delete config._volume_ratio;

      await api('/paper/strategies/create', {
        method: 'POST',
        body: JSON.stringify({
          name: `${rec.strategy_name} — ${rec.market}`,
          strategy_type: rec.strategy_type,
          market: rec.market,
          config: config,
          interval_seconds: rec.timeframe === '5m' ? 60 : rec.timeframe === '15m' ? 120 : rec.timeframe === '1h' ? 300 : rec.timeframe === '4h' ? 900 : 3600,
        }),
      });
      await loadStrategies();
      setSuccess(`Strategie "${rec.strategy_name} — ${rec.market}" erstellt!`);
    } catch (e: any) {
      setError(e.message || 'Strategie konnte nicht erstellt werden');
    }
  };

  // --- Helpers ---

  const getTypeDef = (type: string) => STRATEGY_TYPES.find(t => t.value === type);
  const fmtEur = (v: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v);

  // --- Render ---

  return (
    <>
      <Helmet><title>Strategien - btrader</title></Helmet>
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
          <Typography variant="h5" component="h1" fontWeight={700}>Strategien</Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            {runnerStatus && (
              <Chip
                icon={runnerStatus.running ? <FavoriteIcon sx={{ animation: 'pulse 1s infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} /> : undefined}
                label={runnerStatus.running ? 'Auto-Trader läuft' : 'Auto-Trader gestoppt'}
                color={runnerStatus.running ? 'success' : 'default'}
                variant={runnerStatus.running ? 'filled' : 'outlined'}
                size="small"
                sx={{ mr: 1 }}
              />
            )}
            <Tooltip title="Alle aktiven Strategien jetzt manuell prüfen und bei Signal handeln" arrow>
              <span>
                <Button
                  variant="outlined"
                  startIcon={executingAll ? <CircularProgress size={18} /> : <RunAllIcon />}
                  onClick={handleExecuteAll}
                  disabled={executingAll || strategies.filter(s => s.is_active).length === 0}
                >
                  Alle ausführen
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="KI analysiert alle Märkte und empfiehlt die beste Strategie für aktuelle Bedingungen" arrow>
              <span>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={adviceLoading ? <CircularProgress size={16} /> : <TipsAndUpdatesIcon />}
                  onClick={handleBestAdvice}
                  disabled={adviceLoading}
                >
                  {adviceLoading ? 'Scanne Märkte...' : 'Markt-Empfehlung'}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Neue Trading-Strategie erstellen mit Markt, Budget und Risikomanagement" arrow>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
                Neue Strategie
              </Button>
            </Tooltip>
          </Stack>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        {/* Depot-Übersicht */}
        {strategies.length > 0 && (() => {
          const num = (v: any) => (typeof v === 'number' && isFinite(v)) ? v : (parseFloat(v) || 0);
          const totalInvested = strategies.reduce((sum, s) => sum + num(s.budget_eur), 0);
          const totalUsed = strategies.reduce((sum, s) => sum + num(s.budget_used_eur), 0);
          const totalPnL = strategies.reduce((sum, s) => sum + num(s.total_profit_loss), 0);
          const totalTrades = strategies.reduce((sum, s) => sum + num(s.total_trades), 0);
          const totalWins = strategies.reduce((sum, s) => sum + num(s.winning_trades), 0);
          const overallWinRate = totalTrades > 0 ? (totalWins / totalTrades * 100) : 0;
          const pnlPct = totalUsed > 0 ? (totalPnL / totalUsed * 100) : 0;
          const activeCount = strategies.filter(s => s.is_active).length;

          return (
            <Card sx={{ borderRadius: 3, mb: 3, background: 'linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(76,175,80,0.08) 100%)' }}>
              <CardContent sx={{ py: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Gesamt-P&L</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ color: totalPnL >= 0 ? 'success.main' : 'error.main' }}>
                      {totalPnL >= 0 ? '+' : ''}{fmtEur(totalPnL)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: pnlPct >= 0 ? 'success.main' : 'error.main' }}>
                      {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(1)}% auf investiertes Kapital
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Typography variant="caption" color="text.secondary">Investiert</Typography>
                    <Typography variant="subtitle1" fontWeight={600}>{fmtEur(totalUsed)}</Typography>
                    <Typography variant="caption" color="text.secondary">von {fmtEur(totalInvested)} Budget</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Typography variant="caption" color="text.secondary">Trades</Typography>
                    <Typography variant="subtitle1" fontWeight={600}>{totalTrades}</Typography>
                    <Typography variant="caption" color="text.secondary">{totalWins} gewonnen</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Typography variant="caption" color="text.secondary">Win-Rate</Typography>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ color: overallWinRate >= 50 ? 'success.main' : overallWinRate > 0 ? 'warning.main' : 'text.secondary' }}>
                      {overallWinRate.toFixed(1)}%
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3} md={3}>
                    <Typography variant="caption" color="text.secondary">Strategien</Typography>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {activeCount} aktiv / {strategies.length} gesamt
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          );
        })()}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : strategies.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>Keine Strategien vorhanden</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Erstelle deine erste Trading-Strategie mit Marktauswahl.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>Strategie erstellen</Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {strategies.map(s => {
              const typeDef = getTypeDef(s.strategy_type);
              const winRate = s.total_trades > 0 ? (s.winning_trades / s.total_trades * 100) : 0;
              const isRunning = executing === s.id;
              const slTpDefaults = SL_TP_DEFAULTS[s.strategy_type];
              const rrRatio = (s.take_profit_percentage && s.stop_loss_percentage) ? (s.take_profit_percentage / s.stop_loss_percentage) : null;
              const val = valuations[String(s.id)];

              return (
                <Grid item xs={12} md={6} key={s.id}>
                  <Card elevation={1} sx={{
                    display: 'flex', flexDirection: 'column', height: '100%',
                    borderRadius: 3,
                    borderTop: 3, borderColor: typeDef?.color || 'grey.400',
                    transition: 'all 0.2s ease',
                    '&:hover': { boxShadow: 8, transform: 'translateY(-2px)' },
                  }}>
                    <CardContent sx={{ flexGrow: 1, pb: 1, px: 2.5, pt: 2.5 }}>
                      {/* Header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Tooltip title={typeDef?.description || s.strategy_type} arrow>
                            <Box sx={{ color: typeDef?.color, fontSize: '1.3rem', display: 'flex', cursor: 'help' }}>{typeDef?.icon}</Box>
                          </Tooltip>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>{s.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{typeDef?.label || s.strategy_type}</Typography>
                          </Box>
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Gehandelter Markt" arrow><Chip label={s.config.market || '?'} size="small" color="primary" /></Tooltip>
                          {s.is_live && <Tooltip title="Echtes Geld — Live-Trading aktiv!" arrow><Chip label="LIVE" size="small" color="error" sx={{ fontWeight: 700 }} /></Tooltip>}
                          <Tooltip title={s.is_active ? 'Strategie wird automatisch geprüft' : 'Strategie ist pausiert'} arrow>
                            <Chip label={s.is_active ? 'Aktiv' : 'Pause'} size="small" color={s.is_active ? 'success' : 'default'} variant={s.is_active ? 'filled' : 'outlined'} />
                          </Tooltip>
                        </Stack>
                      </Box>

                      <Divider sx={{ mb: 1.5 }} />

                      {/* Depot-Wert */}
                      {val && val.budget_eur > 0 && (
                        <Box sx={{ mb: 1.5, p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <Typography variant="caption" color="text.secondary">Depot-Wert</Typography>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="subtitle2" fontWeight={700}>{fmtEur(val.current_value_eur)}</Typography>
                              <Typography variant="caption" fontWeight={600} sx={{ color: val.pnl_percent >= 0 ? 'success.main' : 'error.main' }}>
                                {val.pnl_percent >= 0 ? '+' : ''}{val.pnl_percent.toFixed(2)}%
                              </Typography>
                            </Box>
                          </Box>
                          {val.holdings > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.6rem' }}>
                              {val.holdings.toFixed(6)} {val.market.split('-')[0]} × {val.market_price != null ? fmtEur(val.market_price) : '—'}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* 2-spaltig: Risiko links, Performance rechts */}
                      <Grid container spacing={2}>
                        {/* Risiko & Budget */}
                        <Grid item xs={6}>
                          <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.6rem', letterSpacing: 1 }}>Risiko & Budget</Typography>
                          <Stack spacing={0.3} sx={{ mt: 0.5 }}>
                            <Tooltip title={slTpDefaults?.slTip || 'Automatischer Verkauf bei Kursverlust — schützt dein Kapital'} arrow placement="right">
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', cursor: 'help' }}>
                                <Typography variant="caption" color="text.secondary">Stop-Loss</Typography>
                                <Typography variant="caption" fontWeight={600} color="error.main">
                                  {s.stop_loss_percentage != null ? `${s.stop_loss_percentage}%` : '—'}
                                </Typography>
                              </Box>
                            </Tooltip>
                            <Tooltip title={slTpDefaults?.tpTip || 'Automatischer Verkauf bei Kursgewinn — sichert deine Gewinne'} arrow placement="right">
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', cursor: 'help' }}>
                                <Typography variant="caption" color="text.secondary">Take-Profit</Typography>
                                <Typography variant="caption" fontWeight={600} color="success.main">
                                  {s.take_profit_percentage != null ? `${s.take_profit_percentage}%` : '—'}
                                </Typography>
                              </Box>
                            </Tooltip>
                            {rrRatio && (
                              <Tooltip title={`Risk/Reward 1:${rrRatio.toFixed(1)} — für jeden €1 Risiko sind €${rrRatio.toFixed(1)} Gewinn möglich. Ab 1:1.5 gilt als gut.`} arrow placement="right">
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', cursor: 'help' }}>
                                  <Typography variant="caption" color="text.secondary">R:R</Typography>
                                  <Typography variant="caption" fontWeight={600} color={rrRatio >= 1.5 ? 'success.main' : 'warning.main'}>
                                    1:{rrRatio.toFixed(1)}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}
                            <Tooltip title="Wie oft die Strategie den Markt prüft und Signale generiert" arrow placement="right">
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', cursor: 'help' }}>
                                <Typography variant="caption" color="text.secondary">Intervall</Typography>
                                <Typography variant="caption" fontWeight={600}>{formatInterval(s.interval_seconds || 300)}</Typography>
                              </Box>
                            </Tooltip>
                            <Tooltip title="Maximaler EUR-Betrag den diese Strategie einsetzen darf" arrow placement="right">
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', cursor: 'help' }}>
                                <Typography variant="caption" color="text.secondary">Budget</Typography>
                                <Typography variant="caption" fontWeight={600}>
                                  {s.budget_eur != null && s.budget_eur > 0 ? fmtEur(s.budget_eur) : '—'}
                                </Typography>
                              </Box>
                            </Tooltip>
                            {s.budget_eur != null && s.budget_eur > 0 && (
                              <Tooltip title={`${fmtEur(s.budget_used_eur || 0)} von ${fmtEur(s.budget_eur)} verbraucht (${((s.budget_used_eur || 0) / s.budget_eur * 100).toFixed(0)}%)`} arrow placement="right">
                                <Box sx={{ cursor: 'help' }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.min(100, ((s.budget_used_eur || 0) / s.budget_eur) * 100)}
                                    sx={{ height: 4, borderRadius: 3, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { bgcolor: ((s.budget_used_eur || 0) / s.budget_eur) > 0.9 ? 'error.main' : 'primary.main', borderRadius: 3 } }}
                                  />
                                </Box>
                              </Tooltip>
                            )}
                          </Stack>
                        </Grid>

                        {/* Performance */}
                        <Grid item xs={6}>
                          <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.6rem', letterSpacing: 1 }}>Performance</Typography>
                          <Stack spacing={0.3} sx={{ mt: 0.5 }}>
                            <Tooltip title="Gesamtanzahl der ausgeführten Trades (Käufe + Verkäufe)" arrow placement="left">
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', cursor: 'help' }}>
                                <Typography variant="caption" color="text.secondary">Trades</Typography>
                                <Typography variant="caption" fontWeight={600}>{s.total_trades}</Typography>
                              </Box>
                            </Tooltip>
                            <Tooltip title={`${s.winning_trades} von ${s.total_trades} Trades mit Gewinn abgeschlossen`} arrow placement="left">
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', cursor: 'help' }}>
                                <Typography variant="caption" color="text.secondary">Gewonnen</Typography>
                                <Typography variant="caption" fontWeight={600} color="success.main">{s.winning_trades}</Typography>
                              </Box>
                            </Tooltip>
                            <Tooltip title={`Win-Rate: ${winRate.toFixed(1)}% — ab 50% profitabel wenn R:R > 1:1`} arrow placement="left">
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', cursor: 'help' }}>
                                <Typography variant="caption" color="text.secondary">Win-Rate</Typography>
                                <Typography variant="caption" fontWeight={600} color={winRate >= 50 ? 'success.main' : winRate > 0 ? 'warning.main' : 'text.secondary'}>
                                  {winRate.toFixed(1)}%
                                </Typography>
                              </Box>
                            </Tooltip>
                            <Tooltip title={`Gesamter Profit/Verlust aller Trades dieser Strategie (nach Gebühren)`} arrow placement="left">
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', cursor: 'help' }}>
                                <Typography variant="caption" color="text.secondary">P&L</Typography>
                                <Typography variant="caption" fontWeight={700} sx={{ color: s.total_profit_loss >= 0 ? 'success.main' : 'error.main' }}>
                                  {s.total_profit_loss >= 0 ? '+' : ''}{fmtEur(s.total_profit_loss)}
                                </Typography>
                              </Box>
                            </Tooltip>
                            {s.total_trades > 0 && (
                              <Tooltip title={`Win-Rate Balken: ${winRate.toFixed(1)}% Grün = Gewinn, Rot = Verlust`} arrow placement="left">
                                <Box sx={{ cursor: 'help' }}>
                                  <LinearProgress variant="determinate" value={winRate} sx={{ height: 4, borderRadius: 3, bgcolor: 'error.dark', '& .MuiLinearProgress-bar': { bgcolor: 'success.main', borderRadius: 3 } }} />
                                </Box>
                              </Tooltip>
                            )}
                          </Stack>
                          <Tooltip title="Zeitpunkt der letzten automatischen Marktprüfung" arrow placement="left">
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontSize: '0.6rem', cursor: 'help' }}>
                              Geprüft: {s.last_executed ? new Date(s.last_executed).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : (runnerStatus?.running && s.is_active ? 'Wartet...' : 'Nie')}
                            </Typography>
                          </Tooltip>
                        </Grid>
                      </Grid>

                      {/* Letztes Signal */}
                      <StrategySignalDisplay signal={s.last_signal} signalAt={s.last_signal_at} />

                      {lastResult?.id === s.id && (
                        <Alert severity={lastResult.result.success ? 'success' : 'warning'} sx={{ mt: 1, py: 0 }}>
                          <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                            {lastResult.result.signals.length} Signal(e), {lastResult.result.trades_executed.length} Trade(s)
                            {lastResult.result.reason && `\n${lastResult.result.reason}`}
                          </Typography>
                        </Alert>
                      )}
                    </CardContent>

                    {/* Aktionsleiste */}
                    <CardActions sx={{ px: 2.5, pb: 2, pt: 1, justifyContent: 'space-between', borderTop: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={s.is_active ? 'Strategie pausieren — keine automatischen Trades mehr' : 'Strategie aktivieren — Auto-Trader prüft diesen Markt'} arrow>
                          <Switch size="small" checked={s.is_active} onChange={() => handleToggleActive(s)} />
                        </Tooltip>
                        <Tooltip title={s.is_live ? 'LIVE: Echtes Geld! Umschalten auf Paper-Modus' : 'PAPER: Simuliert. Umschalten auf Live-Trading (benötigt API-Key + 2FA)'} arrow>
                          <FormControlLabel
                            control={<Switch checked={s.is_live || false} onChange={() => handleToggleLive(s.id, !(s.is_live || false))} color="error" size="small" />}
                            label={<Chip label={s.is_live ? 'LIVE' : 'PAPER'} size="small" color={s.is_live ? 'error' : 'default'} variant={s.is_live ? 'filled' : 'outlined'} sx={{ fontSize: '0.65rem', height: 20 }} />}
                            sx={{ ml: 0, mr: 0.5 }}
                          />
                        </Tooltip>
                        <Tooltip title="Strategie-Einstellungen bearbeiten (Name, Markt, Budget, SL/TP)" arrow>
                          <IconButton size="small" onClick={() => openEditDialog(s)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Strategie unwiderruflich löschen" arrow>
                          <IconButton size="small" color="error" onClick={() => handleDelete(s.id, s.name)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Detaillierte Marktanalyse mit Charts, Indikatoren und erkannten Mustern anzeigen" arrow>
                          <Button size="small" variant="outlined" startIcon={<AnalyzeIcon />} onClick={() => openAnalysis(s)} color="info">Analyse</Button>
                        </Tooltip>
                        <Tooltip title="Strategie jetzt manuell ausführen — prüft den Markt und handelt bei Signal" arrow>
                          <span><Button size="small" variant="contained" startIcon={isRunning ? <CircularProgress size={16} /> : <RunIcon />} onClick={() => handleExecute(s.id)} disabled={isRunning || !s.is_active}>Ausführen</Button></span>
                        </Tooltip>
                      </Stack>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Create / Edit Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingStrategy ? 'Strategie bearbeiten' : 'Neue Strategie erstellen'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <TextField
                label="Name"
                value={formName}
                onChange={e => setFormName(e.target.value)}
                fullWidth
                placeholder="z.B. BTC Trend Reiter"
              />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Markt</InputLabel>
                    <Select value={formMarket} label="Markt" onChange={e => setFormMarket(e.target.value)}>
                      {MARKETS.map(m => {
                        const vol = marketVolatility[m];
                        return (
                          <MenuItem key={m} value={m}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                              <span>{m}</span>
                              {vol && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                                  <Chip
                                    label={`${vol.volatility_pct}% ${vol.label}`}
                                    size="small"
                                    color={vol.color as any}
                                    variant="outlined"
                                    sx={{ fontSize: '0.65rem', height: 20 }}
                                  />
                                  <Typography
                                    variant="caption"
                                    color={vol.change_24h >= 0 ? 'success.main' : 'error.main'}
                                    sx={{ fontSize: '0.65rem', fontWeight: 600 }}
                                  >
                                    {vol.change_24h >= 0 ? '+' : ''}{vol.change_24h}%
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Strategie-Typ</InputLabel>
                    <Select value={formType} label="Strategie-Typ" onChange={e => handleTypeChange(e.target.value)}>
                      {STRATEGY_TYPES.map(t => (
                        <MenuItem key={t.value} value={t.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ color: t.color, display: 'flex' }}>{t.icon}</Box>
                            <Typography variant="body2" fontWeight={600}>{t.label}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Intervall */}
              <FormControl fullWidth>
                <InputLabel>Prüf-Intervall</InputLabel>
                <Select value={formInterval} label="Prüf-Intervall" onChange={e => setFormInterval(Number(e.target.value))}>
                  {INTERVAL_OPTIONS.map(o => (
                    <MenuItem key={o.value} value={o.value}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <Typography variant="body2">{o.label}</Typography>
                        {STRATEGY_TYPES.find(t => t.value === formType)?.defaultInterval === o.value && (
                          <Chip label="Empfohlen" size="small" color="primary" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                  {(() => {
                    const typeDef = STRATEGY_TYPES.find(t => t.value === formType);
                    return typeDef ? (
                      <>Kerzen-Zeitrahmen: <strong>{typeDef.candles}</strong> — empfohlenes Intervall: <strong>{formatInterval(typeDef.defaultInterval)}</strong></>
                    ) : 'Wie oft die Strategie den Markt analysiert';
                  })()}
                </Typography>
              </FormControl>

              {editingStrategy?.is_active && formType !== editingStrategy.strategy_type && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  Strategie-Typ wird geändert — die Konfiguration wird auf Standardwerte zurückgesetzt.
                </Alert>
              )}

              {/* Budget & Risk Management */}
              <Divider><Chip label="Budget & Risiko" size="small" /></Divider>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Budget (EUR)"
                    value={formBudget}
                    onChange={e => setFormBudget(e.target.value)}
                    fullWidth
                    type="number"
                    placeholder="z.B. 500"
                    inputProps={{ min: 0, step: 50 }}
                    helperText="Max. Einsatz für diese Strategie"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Tooltip title={SL_TP_DEFAULTS[formType]?.slTip || 'Automatischer Verkauf wenn der Kurs um diesen Prozentsatz fällt. Engerer Stop = weniger Risiko aber öfter ausgelöst.'} arrow placement="top">
                    <TextField
                      label="Stop-Loss (%)"
                      value={formStopLoss}
                      onChange={e => setFormStopLoss(e.target.value)}
                      fullWidth
                      type="number"
                      placeholder="z.B. 5"
                      inputProps={{ min: 0, max: 50, step: 0.5 }}
                      helperText={SL_TP_DEFAULTS[formType] ? `Empfohlen: ${SL_TP_DEFAULTS[formType].sl}%` : 'Automatischer Verkauf bei Verlust'}
                    />
                  </Tooltip>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Tooltip title={SL_TP_DEFAULTS[formType]?.tpTip || 'Automatischer Verkauf wenn der Kurs um diesen Prozentsatz steigt. Sichert Gewinne bevor der Kurs wieder fällt.'} arrow placement="top">
                    <TextField
                      label="Take-Profit (%)"
                      value={formTakeProfit}
                      onChange={e => setFormTakeProfit(e.target.value)}
                      fullWidth
                      type="number"
                      placeholder="z.B. 10"
                      inputProps={{ min: 0, max: 100, step: 0.5 }}
                      helperText={SL_TP_DEFAULTS[formType] ? `Empfohlen: ${SL_TP_DEFAULTS[formType].tp}%` : 'Automatischer Verkauf bei Gewinn'}
                    />
                  </Tooltip>
                </Grid>
              </Grid>

              {/* Mindest-Trade-Betrag */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Tooltip
                    title="Bitvavo nimmt 0,25% Gebühr pro Trade. Bei kleinen Beträgen frisst das die Rendite. Mit dem Mindest-Trade-Betrag stellst du sicher, dass nur lohnende Orders ausgeführt werden. Bitvavo-Mindest-Orderwert: 5 EUR. 0 = kein Limit."
                    arrow
                    placement="top"
                  >
                    <TextField
                      label="Mindest-Trade-Betrag (EUR)"
                      value={formMinTradeAmount}
                      onChange={e => setFormMinTradeAmount(e.target.value)}
                      fullWidth
                      type="number"
                      placeholder="z.B. 50"
                      inputProps={{ min: 0, step: 5 }}
                      helperText={
                        formMinTradeAmount && parseFloat(formMinTradeAmount) > 0 && parseFloat(formMinTradeAmount) < 5
                          ? 'Mindestens 5 EUR (Bitvavo-Limit)'
                          : 'Verhindert Mikro-Trades, die durch Gebühren unprofitabel werden. 0 oder leer = kein Limit'
                      }
                      error={!!(formMinTradeAmount && parseFloat(formMinTradeAmount) > 0 && parseFloat(formMinTradeAmount) < 5)}
                    />
                  </Tooltip>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!formMinTradeAmount || parseFloat(formMinTradeAmount) <= 0}>
                    <InputLabel>Verhalten unter Mindestbetrag</InputLabel>
                    <Select
                      value={formMinTradeBehavior}
                      label="Verhalten unter Mindestbetrag"
                      onChange={e => setFormMinTradeBehavior(e.target.value as 'skip' | 'round_up')}
                    >
                      <MenuItem value="skip">Trade überspringen</MenuItem>
                      <MenuItem value="round_up">Auf Mindestbetrag aufrunden</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {formMinTradeAmount && parseFloat(formMinTradeAmount) >= 5 && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  {formMinTradeBehavior === 'skip'
                    ? `Wenn ein Signal weniger als €${formMinTradeAmount} handeln will, wird der Trade übersprungen — der Bot wartet auf ein stärkeres Signal.`
                    : `Wenn ein Signal weniger als €${formMinTradeAmount} handeln will, wird die Order auf €${formMinTradeAmount} aufgestockt (sofern Cash + Budget ausreichen).`}
                </Alert>
              )}

              {/* Bear-Hunter-Modus: nur fuer marktmeister */}
              {formType === 'marktmeister' && (
                <Box sx={{ mt: 1, p: 1.5, border: '1px solid', borderColor: formEnableShorts ? 'warning.main' : 'divider', borderRadius: 1.5, bgcolor: formEnableShorts ? 'rgba(245,158,11,0.06)' : 'transparent' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formEnableShorts}
                        onChange={(e) => setFormEnableShorts(e.target.checked)}
                        color="warning"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Bear-Hunter-Modus (Shorts in Abwärtstrend)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Bei TREND_DOWN-Phasen öffnet die Strategie Short-Positionen statt nur ins Cash zu gehen.
                          Bitvavo-Borrow-Fee gilt. Nur in Paper-Trading nutzbar (Live-Shorts gehen nicht via API).
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', m: 0 }}
                  />
                  {formEnableShorts && (
                    <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                      <strong>Asymmetrisches Risiko:</strong> Stop liegt über Entry, Verlust unbegrenzt bis Liquidation
                      (Entry × 2). Position-Größe wird automatisch halbiert (50% der Long-Größe). Der Optimizer
                      kann beide Varianten testen — ich empfehle, erst im Backtest zu prüfen ob Shorts profitabel sind.
                    </Alert>
                  )}
                </Box>
              )}

              <TextField
                label="Beschreibung (optional)"
                value={formDescription}
                onChange={e => setFormDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />

              {!editingStrategy && (
                <FormControlLabel
                  control={<Switch checked={formActive} onChange={e => setFormActive(e.target.checked)} />}
                  label="Sofort aktivieren"
                />
              )}

              {/* Strategy type description */}
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="caption" color="text.secondary">
                  {STRATEGY_TYPES.find(t => t.value === formType)?.description}
                </Typography>
              </Paper>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !formName.trim() || !formMarket}
              startIcon={saving ? <CircularProgress size={18} /> : undefined}
            >
              {editingStrategy ? 'Speichern' : 'Erstellen'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Strategy Analysis Dialog */}
        {analysisStrategy && (
          <StrategyAnalysis
            market={analysisStrategy.config.market || 'BTC-EUR'}
            strategyType={analysisStrategy.strategy_type}
            strategyName={analysisStrategy.name}
            strategyId={analysisStrategy.id}
            open={analysisOpen}
            onClose={() => setAnalysisOpen(false)}
          />
        )}

        {/* Best Advice Dialog */}
        <Dialog open={adviceDialogOpen} onClose={() => setAdviceDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TipsAndUpdatesIcon color="success" />
              Best Advice — Marktanalyse
            </Box>
          </DialogTitle>
          <DialogContent>
            {adviceLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Scanne {10} Märkte mit {5} Strategien...</Typography>
                <Typography variant="caption" color="text.secondary">Das kann bis zu 30 Sekunden dauern</Typography>
              </Box>
            ) : adviceResults && adviceResults.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Alert severity="success" sx={{ mb: 1 }}>
                  {adviceResults.length} Einstiegspunkt{adviceResults.length > 1 ? 'e' : ''} gefunden!
                </Alert>
                {adviceResults.map((rec: any, idx: number) => (
                  <Card key={idx} variant="outlined" sx={{ borderColor: idx === 0 ? 'success.main' : undefined, borderWidth: idx === 0 ? 2 : 1 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant="h6" fontWeight={700}>
                            {idx === 0 && '\u2B50 '}{rec.strategy_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">{rec.description}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Chip label={rec.market} size="small" color="primary" />
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            Konfidenz: {Math.round((rec.confidence || 0) * 100)}%
                          </Typography>
                        </Box>
                      </Box>

                      <Alert severity="success" sx={{ my: 1 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                          {rec.signal?.reason || rec.reason_short}
                        </Typography>
                      </Alert>

                      {rec.signal?.stop_loss && (
                        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                          <Chip label={`Stop-Loss: \u20AC${rec.signal.stop_loss.toFixed(2)}`} size="small" color="error" variant="outlined" />
                          {rec.signal.take_profit && <Chip label={`Take-Profit: \u20AC${rec.signal.take_profit.toFixed(2)}`} size="small" color="success" variant="outlined" />}
                        </Box>
                      )}

                      {rec.optimized_config?._volatility_pct && (
                        <Typography variant="caption" color="text.secondary">
                          Volatilität: {rec.optimized_config._volatility_pct}% | Volumen: {rec.optimized_config._volume_ratio}x Durchschnitt
                        </Typography>
                      )}

                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={() => {
                            setAdviceDialogOpen(false);
                            handleCreateFromAdvice(rec);
                          }}
                        >
                          Diese Strategie erstellen
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  Aktuell keine klaren Einstiegspunkte gefunden. Die Märkte zeigen keine starken Signale.
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Tipp: Versuche es später erneut — die Märkte ändern sich ständig.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAdviceDialogOpen(false)}>Schließen</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default StrategiesPage;
